import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Brain, BarChart3, Zap } from "lucide-react";

const DURATIONS = [15, 30, 60, 90];

/** Slice 2+3 — host launches Trivia or Quick Poll from Creator Center. */
export default function GamesLaunchSheet({ open, onOpenChange, streamId, initialType = "poll" }) {
  const [type, setType] = useState(initialType);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [correct, setCorrect] = useState(0);
  const [duration, setDuration] = useState(30);
  const [busy, setBusy] = useState(false);
  const [packs, setPacks] = useState(null);
  const [packBusy, setPackBusy] = useState("");

  useEffect(() => {
    if (!open || type !== "trivia" || !streamId) return;
    api.get(`/live/${streamId}/trivia/packs`)
      .then((r) => setPacks(r.data.packs))
      .catch(() => setPacks([]));
  }, [open, type, streamId]);

  const firePack = async (packId) => {
    if (packBusy) return;
    setPackBusy(packId);
    try {
      const { data } = await api.post(`/live/${streamId}/trivia/packs/${packId}/next`, {
        duration_s: duration,
      });
      toast.success(`Trivia started! Question ${data.pack.number}/${data.pack.total}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start pack question.");
    } finally {
      setPackBusy("");
    }
  };

  const launch = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/live/${streamId}/games`, {
        type,
        question: question.trim(),
        options: options.filter((o) => o.trim()),
        correct_index: type === "trivia" ? correct : undefined,
        duration_s: duration,
      });
      toast.success(type === "trivia" ? "Trivia started!" : "Poll started!");
      setQuestion("");
      setOptions(["", ""]);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start game.");
    } finally {
      setBusy(false);
    }
  };

  const valid = question.trim() && options.filter((o) => o.trim()).length >= 2;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="games-launch-sheet"
        className="z-[120] bg-zinc-950/95 backdrop-blur-xl border-t border-white/10 text-white rounded-t-2xl max-h-[80vh] overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle className="text-white text-sm uppercase tracking-widest">Start a Game</SheetTitle>
        </SheetHeader>

        <div className="mt-3 space-y-4 pb-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="game-type-poll"
              onClick={() => setType("poll")}
              className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm ${
                type === "poll" ? "bg-yellow-400/10 border-yellow-400 text-yellow-300" : "bg-white/5 border-white/10"
              }`}
            >
              <BarChart3 size={16} /> Quick Poll
            </button>
            <button
              type="button"
              data-testid="game-type-trivia"
              onClick={() => setType("trivia")}
              className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm ${
                type === "trivia" ? "bg-yellow-400/10 border-yellow-400 text-yellow-300" : "bg-white/5 border-white/10"
              }`}
            >
              <Brain size={16} /> Trivia
            </button>
          </div>

          {type === "trivia" && (
            <div className="space-y-2" data-testid="trivia-packs-section">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                <Zap size={11} className="text-yellow-400" /> Question Packs — one tap
              </div>
              {packs === null ? (
                <div className="flex items-center gap-2 text-xs text-zinc-500 py-2">
                  <Loader2 size={13} className="animate-spin" /> Loading packs…
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-1.5">
                  {packs.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`trivia-pack-${p.id}`}
                      onClick={() => firePack(p.id)}
                      disabled={p.done || !!packBusy}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left text-sm ${
                        p.done
                          ? "bg-white/[0.02] border-white/5 text-zinc-600"
                          : "bg-white/5 border-white/10 hover:border-yellow-400/40 active:scale-[0.99]"
                      }`}
                    >
                      <span className="text-lg leading-none">{p.emoji}</span>
                      <span className="flex-1 truncate">{p.title}</span>
                      {packBusy === p.id ? (
                        <Loader2 size={13} className="animate-spin text-yellow-300" />
                      ) : (
                        <span className={`text-[10px] ${p.done ? "text-zinc-600" : "text-zinc-400"}`}>
                          {p.done ? "Done ✓" : `${p.used}/${p.total}`}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] text-zinc-500 uppercase tracking-widest">or write your own</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
            </div>
          )}

          <input
            data-testid="game-question-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={type === "trivia" ? "Ask a question…" : "What should we vote on?"}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-yellow-400/50"
          />

          <div className="space-y-2">
            {options.map((o, i) => (
              <div key={i} className="flex items-center gap-2">
                {type === "trivia" && (
                  <button
                    type="button"
                    data-testid={`game-correct-${i}`}
                    onClick={() => setCorrect(i)}
                    className={`w-7 h-7 shrink-0 rounded-full border text-xs ${
                      correct === i ? "bg-emerald-500/20 border-emerald-400 text-emerald-300" : "bg-white/5 border-white/15 text-zinc-500"
                    }`}
                    title="Mark correct answer"
                  >
                    ✓
                  </button>
                )}
                <input
                  data-testid={`game-option-${i}`}
                  value={o}
                  onChange={(e) => setOptions((cur) => cur.map((x, j) => (j === i ? e.target.value : x)))}
                  placeholder={`Option ${i + 1}`}
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yellow-400/50"
                />
              </div>
            ))}
            {options.length < 4 && (
              <button
                type="button"
                data-testid="game-add-option"
                onClick={() => setOptions((cur) => [...cur, ""])}
                className="text-xs text-zinc-400 hover:text-white"
              >
                + Add option
              </button>
            )}
          </div>

          <div className="flex gap-2">
            {DURATIONS.map((d) => (
              <button
                key={d}
                type="button"
                data-testid={`game-duration-${d}`}
                onClick={() => setDuration(d)}
                className={`px-3 py-1.5 rounded-full border text-xs ${
                  duration === d ? "bg-yellow-400/10 border-yellow-400 text-yellow-300" : "bg-white/5 border-white/10"
                }`}
              >
                {d}s
              </button>
            ))}
          </div>

          <button
            type="button"
            data-testid="game-launch-btn"
            onClick={launch}
            disabled={!valid || busy}
            className="w-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50 text-black font-bold py-3 rounded-full text-sm flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : type === "trivia" ? "Start Trivia" : "Start Poll"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
